import React, { memo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Drawer as MuiDrawer, IconButton, AppBar, Toolbar, List } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DashboardOutlined as DashoardIcon,
  HomeOutlined as HomeIcon,
  EvStationOutlined as ChargersIcon,
  HubOutlined as PlazasIcon,
  BarChartOutlined as ReportsIcon,
  BuildOutlined as ToolsIcon,
  Logout as LogoutIcon,
  PersonOutline as PersonIcon,
  UndoOutlined,
  Menu as MenuIcon,
  CloudSyncOutlined as CloudSyncOutlinedIcon,
  DeveloperBoard as DeveloperBoardIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { get, isEqual } from 'lodash';

import { useDispatch } from 'react-redux';
import NavbarMainButton from './NavbarMainButton';
import hasAccess, { adminToolsTabAccessId, isSupervisor, profilePageAccessId, reportsAccessId } from '../../services/HasAccess';
import { isDesktop, isMobile } from '../../services/isMobile';
import { getShowReportsFlag } from '../../services/AuthenticationRules';
import {
  getAdminMenuItems,
  getChargersMenuItems,
  getEdgeMenuItems,
  getExtendedReportsMenuItems,
  getStandardReportsMenuItems,
} from './menuItems';
import { isDashboardAvailable, isHomePageAvailable } from '../appConfig/selector';
import RolesSwitcher from './RolesSwitcher';
import './Navbar.scss';
import { redirectToSupervisor } from '../../../screens/Login/actions';
import { isEdgeDemo } from '../../resources/helper';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const openedArrowMixin = (theme) => ({
  left: drawerWidth,
  transition: theme.transitions.create('left', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedArrowMixin = (theme) => ({
  transition: theme.transitions.create('left', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  left: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    left: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const DesktopDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  zIndex: 1000,

  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const DrawerIcon = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  position: 'fixed',
  top: 20,
  left: drawerWidth,
  marginLeft: '-10px',
  width: 20,
  height: 20,
  color: '#fff',
  border: '1px solid #fff',
  background: '#006064',
  zIndex: 1001,

  '&:hover': {
    backgroundColor: '#006064',
  },

  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },

  ...(open && {
    ...openedArrowMixin(theme),
  }),
  ...(!open && {
    ...closedArrowMixin(theme),
  }),
}));

const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',

  '& .MuiDrawer-paper': {
    width: drawerWidth,
  },
}));

const NavbarLogo = styled('div', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: open ? 190 : 48,
  transition: theme.transitions.create(['width', 'height'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  '& img': {
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  ...(open && {
    '& .big-logo': {
      opacity: 1,
    },
    '& .small-logo': {
      opacity: 0,
    },
  }),
  ...(!open && {
    '& .big-logo': {
      opacity: 0,
    },
    '& .small-logo': {
      opacity: 1,
    },
  }),
}));

const Drawer = isDesktop ? DesktopDrawer : MobileDrawer;

export const getPathDestination = (path) => (path ? path.split('/').length > 0 && path : '/');

export const getActivePathValue = (location) => {
  if (location.pathname.indexOf('/chargers') !== -1 && location.pathname.indexOf('/edit') !== -1) {
    return 'chargers_list';
  }

  return getPathDestination(location.pathname);
};
const getTopLevelPath = (location) => get(location.pathname.match(/([^/]+)/), '0', '');

const Navbar = memo(
  ({ user, isAuthenticated, logout, config, open, onSetOpen, logoutBlockers }) => {
    const location = useLocation();
    const dispatch = useDispatch();

    const [menuOpened, setMenuOpened] = useState(null);
    const initialRoles = get(user, 'initialRoles', []);
    const currentRoles = get(user, 'currentRoles', []);
    const isVisibleSupervisorBackButton =
      initialRoles.some(({ roleName }) => roleName === 'Supervisor') && !currentRoles.some(({ roleName }) => roleName === 'Supervisor');
    const drawerRef = useRef(null);

    const firstName = get(user, 'first_name');
    const reportsAvailable = getShowReportsFlag(config) && hasAccess(reportsAccessId, get(user, 'currentPermissions'));
    const basicReportsMenuItems = getStandardReportsMenuItems(config);
    const extendedReportsMenuItems = getExtendedReportsMenuItems(config);
    const showProfile = hasAccess(profilePageAccessId, user, config);
    const showRoles = !isSupervisor(user);
    const showEdgeMenu = isSupervisor(user) && isEdgeDemo();

    const currentTopLevelPath = getTopLevelPath(location);

    const isExtendedReportsMenu = config.plazaAvailabilityMode === 'full' || config.plazaAvailabilityMode === 'partially';

    const logoNavbarPath = get(config, 'logoNavbar', '');
    const logoIconPath = get(config, 'logoIcon', '');

    const toggleMenu = () => onSetOpen(!open);
    const redirectToSupervisorHandler = () => dispatch(redirectToSupervisor());

    const getProfileSubMenu = () => {
      const subMenu = [];

      if ((initialRoles && initialRoles.length > 1) || !open) {
        subMenu.push({
          CustomComponent: ({ opened }) => (
            <div className="profile-menu">
              {!opened && (
                <span className="greetings">
                  Hi,
                  {firstName}!
                </span>
              )}
              {showRoles && initialRoles && initialRoles.length > 1 ? <RolesSwitcher initialRoles={initialRoles} /> : null}
            </div>
          ),
        });
      }

      if (showProfile) {
        subMenu.push({
          label: 'Profile',
          path: '/profile',
          levelPath: 'profile',
          available: showProfile,
        });
      }

      return subMenu;
    };

    const menuItems = [
      {
        Icon: HomeIcon,
        label: 'Home',
        path: '/',
        levelPath: '',
        available: isHomePageAvailable(config, ''),
      },
      {
        Icon: DashoardIcon,
        label: 'Dashboard',
        path: '/dashboard',
        levelPath: 'dashboard',
        available: isDashboardAvailable(config, ''),
      },
      {
        Icon: ChargersIcon,
        label: 'Chargers',
        levelPath: 'chargers',
        items: getChargersMenuItems(config, user),
      },
      {
        Icon: DeveloperBoardIcon,
        label: 'EDGE',
        path: '/edge',
        levelPath: 'edge',
        overlapIconText: 'Demo',
        items: getEdgeMenuItems(config, user),
        available: showEdgeMenu,
      },
      {
        Icon: PlazasIcon,
        label: 'IoT.ON Edge Plazas',
        path: '/plaza_list',
        levelPath: 'plaza_list',
        available: isExtendedReportsMenu,
      },
      {
        Icon: ReportsIcon,
        label: 'Reports',
        levelPath: 'reports',
        available: reportsAvailable,
        items: [
          {
            CustomComponent: ({ opened }) => <div className={`sub-title reports-title${opened ? ' opened' : ''}`}>Standard reports</div>,
          },
          ...basicReportsMenuItems,
          ...(isExtendedReportsMenu && extendedReportsMenuItems.length
            ? [
                {
                  CustomComponent: ({ opened }) => (
                    <div className={`sub-title reports-title${opened ? ' opened' : ''}`}>IoT.ON Edge Plaza Reports</div>
                  ),
                },
                ...extendedReportsMenuItems,
              ]
            : []),
        ],
      },
      {
        Icon: ToolsIcon,
        label: 'Tools',
        levelPath: 'admin',
        available: hasAccess(adminToolsTabAccessId, get(user, 'currentPermissions')),
        items: getAdminMenuItems(config, user),
      },
    ];

    const profileMenuItems = [
      {
        available: isAuthenticated && isVisibleSupervisorBackButton,
        Icon: UndoOutlined,
        label: 'Back to Supervisor',
        onClick: redirectToSupervisorHandler,
      },
      {
        available: isAuthenticated,
        Icon: PersonIcon,
        label: `Hi, ${firstName}`,
        items: getProfileSubMenu(),
      },
      {
        available: isAuthenticated,
        Icon: LogoutIcon,
        label: 'Sign Out',
        onClick: () => logout(logoutBlockers),
      },
    ];

    if (!isAuthenticated) {
      return null;
    }

    return (
      <>
        {isMobile && (
          <AppBar className="i2p2-nav-bar" position="relative">
            <Toolbar style={{ minHeight: 'unset' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={toggleMenu}
                edge="start"
                sx={{ mr: 2, ...(open && { display: 'none' }) }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        )}
        {isDesktop && (
          <DrawerIcon open={open} onClick={toggleMenu}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </DrawerIcon>
        )}
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={open}
          anchor="left"
          ref={drawerRef}
          onClose={toggleMenu}
          className={`navbar${isDesktop ? ' desktop' : ' mobile'}`}
        >
          <DrawerHeader>
            <NavbarLogo open={open} className="logo-wrapper">
              <img className="big-logo" src={logoNavbarPath} alt="logo" />
              <img className="small-logo" src={logoIconPath} alt="logo" />
            </NavbarLogo>
          </DrawerHeader>
          <List>
            {menuItems
              .filter((item) => get(item, 'available', true))
              .map(({ label, Icon, path, items, levelPath, onClick, overlapIconText }, index) => (
                <NavbarMainButton
                  opened={open}
                  overlapIconText={overlapIconText}
                  openedMenu={menuOpened === label}
                  Icon={Icon}
                  selected={currentTopLevelPath === levelPath}
                  key={label}
                  label={label}
                  path={path}
                  items={items}
                  onClick={onClick}
                  onMenuClick={() => setMenuOpened(menuOpened === label ? null : label)}
                />
              ))}
          </List>
          <List sx={{ marginTop: 'auto', paddingTop: '8px' }}>
            {profileMenuItems
              .filter((item) => get(item, 'available', true))
              .map(({ label, Icon, path, items, levelPath, onClick }, index) => (
                <NavbarMainButton
                  toTop
                  opened={open}
                  openedMenu={menuOpened === label}
                  Icon={Icon}
                  selected={currentTopLevelPath === levelPath}
                  key={label}
                  label={label}
                  path={path}
                  items={items}
                  onClick={onClick}
                  onMenuClick={() => setMenuOpened(menuOpened === label ? null : label)}
                />
              ))}
          </List>
        </Drawer>
      </>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.location, nextProps.location) &&
    isEqual(prevProps.open, nextProps.open) &&
    isEqual(prevProps.config, nextProps.config) &&
    isEqual(prevProps.logout, nextProps.logout) &&
    isEqual(prevProps.user, nextProps.user) &&
    isEqual(prevProps.logoutBlockers, nextProps.logoutBlockers) &&
    isEqual(prevProps.isAuthenticated, nextProps.isAuthenticated),
);

export default Navbar;
